import React from 'react';
import { getApiBase } from './apiBase';

// AriaThinkingStrip — Braeden 8/20: "show me her thinking progress when i
// send a message, and also when i send a stt, because its still so off."
//
// Lives at the bottom of the top-right live chat. Two jobs:
//   1. EARS: the exact words STT locked in — live partials while the mic is
//      hot, then the FINAL text she actually acted on, labeled with which
//      path produced it (streaming Whisper vs cloud). When a reply feels
//      off, this line shows whether she misheard or misthought.
//   2. BRAIN: the pipeline stages as they fire, with timings — heard →
//      match (source + score) → thinking (tier) → speaking. Fed by the same
//      /api/pipeline/events ring buffer the Dev Console reads.
//
// Read-only and self-contained: its own 500ms poll, no shared state, so it
// can never break the voice flow. Fades out 12s after a turn completes.

const STAGE_LABEL = {
  'transcribe.in': 'transcribing',
  'transcribe.out': 'stt',
  'intent.classify': 'intent',
  'hot.embed': 'match',
  'hot.hit': 'match',
  'hot.miss': 'match',
  'brain.match': 'brain',
  'brain.miss': 'brain miss',
  'brain.ask': 'brain',
  'brain.answer': 'brain',
  'brain.bail': 'brain bail',
  'voice.owner': 'voice id',
  'simple.instant': 'reply',
  'teach.start': 'escalate',
  'teach.sonnet.start': 'escalate',
  'teach.sonnet.end': 'escalate',
  'tts.gen.start': 'voice',
  'tts.gen.done': 'voice',
  'response.sent': 'reply',
};

export default function AriaThinkingStrip() {
  const [turn, setTurn] = React.useState(null);
  const sinceRef = React.useRef(0);
  const [, forceTick] = React.useState(0);

  React.useEffect(() => {
    let dead = false;
    const base = String(getApiBase() || '').replace(/\/$/, '');
    async function poll() {
      try {
        const r = await fetch(`${base}/api/pipeline/events?since=${sinceRef.current}`);
        if (!r.ok || dead) return;
        const { events = [] } = await r.json();
        if (!events.length) return;
        for (const e of events) if (e.seq > sinceRef.current) sinceRef.current = e.seq;
        setTurn((prev) => {
          let t = prev;
          for (const e of events) {
            if (e.stage === 'ws.partial') {
              // a fresh partial after a finished turn = a NEW turn starting
              if (!t || t.done) t = { startedAt: e.t, stages: [], partial: '', heard: null, done: false, lastAt: e.t };
              t = { ...t, partial: e.text || '', lastAt: e.t };
            } else if (e.stage === 'ws.final' || e.stage === 'conversation.in') {
              if (!t || t.done) t = { startedAt: e.t, stages: [], partial: '', heard: null, done: false, lastAt: e.t };
              const text = e.text || t.partial || '';
              if (text) t = { ...t, partial: '', heard: { text, via: e.stage === 'ws.final' ? 'streaming' : 'cloud' }, lastAt: e.t };
            } else if (STAGE_LABEL[e.stage]) {
              if (!t || t.done) t = { startedAt: e.t, stages: [], partial: '', heard: null, done: false, lastAt: e.t };
              const label = STAGE_LABEL[e.stage];
              const stages = t.stages.filter((s) => s.label !== label);
              const detailBits = [];
              if (e.stage === 'brain.match') {
                const score = e.meta?.score ?? e.meta?.confidence;
                if (e.meta?.mode || e.meta?.source) detailBits.push(e.meta.mode || e.meta.source);
                if (typeof score === 'number') detailBits.push(score.toFixed(2));
              }
              if (e.stage === 'response.sent' && (e.meta?.tier || e.meta?.source)) detailBits.push(e.meta.tier || e.meta.source);
              const ms = e.meta?.totalMs || e.meta?.ms || null;
              stages.push({ label, detail: detailBits.join(' '), ms, t: e.t });
              t = { ...t, stages, lastAt: e.t, done: e.stage === 'response.sent' ? true : t.done };
            }
          }
          return t;
        });
      } catch (_) {}
    }
    const id = setInterval(poll, 500);
    const tick = setInterval(() => forceTick((n) => n + 1), 400); // keeps the live ms counter moving
    poll();
    return () => { dead = true; clearInterval(id); clearInterval(tick); };
  }, []);

  if (!turn) return null;
  const age = Date.now() - (turn.lastAt || 0);
  if (turn.done && age > 12000) return null;

  const chip = (s, active) => (
    <span key={s.label} style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 999, fontSize: 11, whiteSpace: 'nowrap',
      background: active ? 'rgba(255,170,58,0.18)' : 'rgba(58,255,122,0.10)',
      color: active ? '#ffaa3a' : '#7ee2a0',
      border: `1px solid ${active ? 'rgba(255,170,58,0.35)' : 'rgba(58,255,122,0.18)'}`,
      animation: active ? 'pulse 1.2s ease-in-out infinite' : 'none',
    }}>
      {!active && '✓'} {s.label}{s.detail ? ` ${s.detail}` : ''}{s.ms != null ? ` · ${s.ms}ms` : ''}
    </span>
  );

  const working = !turn.done;
  const elapsed = working && turn.startedAt ? Math.max(0, Math.round((Date.now() - turn.startedAt) / 100) / 10) : null;

  return (
    <div style={{
      pointerEvents: 'none',
      background: 'rgba(10,12,18,0.82)', backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12,
      padding: '8px 12px', fontSize: 12, color: 'rgba(255,255,255,0.85)',
      display: 'flex', flexDirection: 'column', gap: 6,
      opacity: turn.done ? 0.75 : 1, transition: 'opacity .4s',
    }}>
      {turn.partial ? (
        <div style={{ fontStyle: 'italic', color: '#a3c0ff' }}>
          🎙 hearing: {turn.partial}
        </div>
      ) : null}
      {turn.heard ? (
        <div>
          <span style={{ opacity: 0.55 }}>heard ({turn.heard.via}): </span>
          <span style={{ color: '#cfe0ff' }}>&ldquo;{turn.heard.text}&rdquo;</span>
        </div>
      ) : null}
      {(turn.stages.length > 0 || working) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'center' }}>
          {turn.stages.map((s, i) => chip(s, working && i === turn.stages.length - 1))}
          {working && (
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>
              {turn.stages.length === 0 ? 'working…' : ''}{elapsed != null ? ` ${elapsed.toFixed(1)}s` : ''}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
